import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiBookOpen,
    FiSearch,
    FiRefreshCw,
    FiEye,
    FiTrendingUp,
} from "react-icons/fi";

import subjectService from "../../services/subjectService";
import courseService from "../../services/courseService";
import ConfirmModal from "../../components/common/ConfirmModal";
import { getYearOptions, getSemesterOptions } from "../../utils/courseYears";

// ─── Constants ────────────────────────────────────────────────────────────────
const TYPE_OPTIONS = ["Theory", "Practical", "Elective", "Project", "Other"];

const COURSE_COLORS = {
    "D. PHARM": "#003b67",
    "B. PHARM": "#059669",
    "M. PHARM": "#7c3aed",
    "D. Pharm": "#003b67",
    "B. Pharm": "#059669",
    "M. Pharm": "#7c3aed",
};

const TYPE_STYLES = {
    Theory: { bg: "#e8f1f8", color: "#002747" },
    Practical: { bg: "#dcfce7", color: "#15803d" },
    Elective: { bg: "#fef9c3", color: "#92400e" },
    Project: { bg: "#fce7f3", color: "#9d174d" },
    Other: { bg: "#f3f4f6", color: "#6b7280" },
};

const asArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    return [];
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function SubjectList() {
    const navigate = useNavigate();

    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [courses, setCourses] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Filters
    const [search, setSearch] = useState("");
    const [courseFilter, setCourseFilter] = useState("");
    const [yearNumberFilter, setYearNumberFilter] = useState("");
    const [semesterNumberFilter, setSemesterNumberFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");

    useEffect(() => {
        courseService
            .getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch((err) => toast.error("Couldn't load courses: " + err.message));
    }, []);

    const getCourseName = useCallback((courseVal) => {
        if (!courseVal) return "—";
        if (typeof courseVal === "object") {
            return courseVal.shortName || courseVal.courseName || "—";
        }
        const match = courses.find((c) => c._id === courseVal || c.shortName === courseVal || c.courseName === courseVal);
        if (match) return match.shortName || match.courseName;
        return courseVal || "—";
    }, [courses]);

    const yearOptions = useMemo(() => {
        const selectedCourse = courses.find((course) => course._id === courseFilter);
        if (selectedCourse) return getYearOptions(selectedCourse);
        return [];
    }, [courses, courseFilter]);

    const semesterOptions = useMemo(() => {
        const selectedCourse = courses.find((course) => course._id === courseFilter);
        if (selectedCourse) return getSemesterOptions(selectedCourse, yearNumberFilter);
        return [];
    }, [courses, courseFilter, yearNumberFilter]);

    const fetchSubjects = useCallback(async () => {
        setLoading(true);
        try {
            const subRes = await subjectService.getAll({
                course: courseFilter,
                yearNumber: yearNumberFilter,
                semesterNumber: semesterNumberFilter,
                type: typeFilter,
                search,
            });
            setSubjects(asArray(subRes));
        } catch (err) {
            setSubjects([]);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [courseFilter, yearNumberFilter, semesterNumberFilter, typeFilter, search]);

    useEffect(() => {
        fetchSubjects();
    }, [fetchSubjects]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await subjectService.remove(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchSubjects();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const clearFilters = () => {
        setSearch("");
        setCourseFilter("");
        setYearNumberFilter("");
        setSemesterNumberFilter("");
        setTypeFilter("");
    };
    const hasFilters = Boolean(
        search || courseFilter || yearNumberFilter || semesterNumberFilter || typeFilter,
    );

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">Subjects & Syllabus</h1>
                    <p className="page-subtitle">
                        {subjects.length} subject{subjects.length !== 1 ? "s" : ""} found
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/subjects/performance")}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiTrendingUp color="#003b67" size={14} /> Faculty Performance
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/subjects/new")}
                    >
                        <FiPlus size={14} /> Add Subject
                    </button>
                </div>
            </div>


            <div className="card">
                {/* ── Filters ── */}
                <div
                    style={{
                        padding: "0.75rem 1rem",
                        borderBottom: "1px solid var(--border)",
                        display: "flex",
                        gap: "0.65rem",
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    <div className="search-bar">
                        <FiSearch size={14} color="#9ca3af" />
                        <input
                            placeholder="Search by name or code…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="form-control"
                        style={{ width: 160, fontSize: 13 }}
                        value={courseFilter}
                        onChange={(e) => {
                            setCourseFilter(e.target.value);
                            setYearNumberFilter("");
                            setSemesterNumberFilter("");
                        }}
                    >
                        <option value="">All Courses</option>
                        {courses.map((c) => (
                            <option key={c._id} value={c._id}>
                                {c.shortName}
                            </option>
                        ))}
                    </select>
                    <select
                        className="form-control"
                        style={{ width: 145, fontSize: 13 }}
                        value={yearNumberFilter}
                        onChange={(e) => {
                            setYearNumberFilter(e.target.value);
                            setSemesterNumberFilter("");
                        }}
                        disabled={!courseFilter}
                    >
                        <option value="">All Years</option>
                        {yearOptions.map((y) => (
                            <option key={y.yearNumber} value={y.yearNumber}>
                                {y.label}
                            </option>
                        ))}
                    </select>
                    <select
                        className="form-control"
                        style={{ width: 145, fontSize: 13 }}
                        value={semesterNumberFilter}
                        onChange={(e) => setSemesterNumberFilter(e.target.value)}
                        disabled={!courseFilter || semesterOptions.length === 0}
                    >
                        <option value="">All Semesters</option>
                        {semesterOptions.map((s) => (
                            <option key={s.semesterNumber} value={s.semesterNumber}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                    <select
                        className="form-control"
                        style={{ width: 145, fontSize: 13 }}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        <option value="">All Types</option>
                        {TYPE_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                    <button
                        className="btn btn-outline btn-sm"
                        onClick={fetchSubjects}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiRefreshCw size={12} /> Refresh
                    </button>
                    {hasFilters && (
                        <button className="btn btn-outline btn-sm" onClick={clearFilters}>
                            Clear
                        </button>
                    )}
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="loading-center">
                        <div className="spinner" />
                        Loading subjects…
                    </div>
                ) : subjects.length === 0 ? (
                    <div className="empty-state">
                        <FiBookOpen
                            size={48}
                            style={{
                                opacity: 0.25,
                                display: "block",
                                margin: "0 auto 0.75rem",
                            }}
                        />
                        <p>
                            {hasFilters
                                ? "No subjects match your filters."
                                : "No subjects yet. Add one to get started."}
                        </p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Subject</th>
                                    <th>Code</th>
                                    <th>Course</th>
                                    <th>Year / Sem</th>
                                    <th style={{ textAlign: "center" }}>Type</th>
                                    <th style={{ textAlign: "center" }}>Credits</th>
                                    <th style={{ textAlign: "center" }}>Hrs/Week</th>
                                    <th style={{ textAlign: "center" }}>Syllabus</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subjects.map((sub, i) => {
                                    const ts = TYPE_STYLES[sub.type] || TYPE_STYLES.Other;
                                    const courseLabel = getCourseName(sub.course);
                                    const color = COURSE_COLORS[courseLabel] || COURSE_COLORS[courseLabel?.toUpperCase()] || "#6b7280";
                                    return (
                                        <tr key={sub._id}>
                                            <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                {i + 1}
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 600, fontSize: 13 }}>
                                                    {sub.name}
                                                </div>
                                                {sub.description && (
                                                    <div
                                                        style={{
                                                            fontSize: 11.5,
                                                            color: "var(--text-muted)",
                                                            marginTop: 1,
                                                            maxWidth: 220,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {sub.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                {sub.code ? (
                                                    <span className="badge badge-blue">{sub.code}</span>
                                                ) : (
                                                    <span
                                                        style={{ color: "var(--text-muted)", fontSize: 12 }}
                                                    >
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span
                                                    style={{
                                                        background: `${color}18`,
                                                        color,
                                                        padding: "2px 9px",
                                                        borderRadius: 99,
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {courseLabel}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 13 }}>
                                                <div>{sub.yearNumber ? `${sub.yearNumber}${sub.yearNumber === 1 ? "st" : sub.yearNumber === 2 ? "nd" : sub.yearNumber === 3 ? "rd" : "th"} Year` : "—"}</div>
                                                {sub.semesterNumber && (
                                                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>
                                                        Sem {sub.semesterNumber}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <span
                                                    style={{
                                                        ...ts,
                                                        padding: "2px 9px",
                                                        borderRadius: 99,
                                                        fontSize: 11.5,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {sub.type}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: "center", fontSize: 13 }}>
                                                {sub.creditHours > 0 ? (
                                                    sub.creditHours
                                                ) : (
                                                    <span style={{ color: "var(--text-muted)" }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: "center", fontSize: 13 }}>
                                                {sub.weeklyHours > 0 ? (
                                                    sub.weeklyHours
                                                ) : (
                                                    <span style={{ color: "var(--text-muted)" }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {sub.syllabusUnitCount > 0 || sub.hasSyllabus || sub.syllabus?.length > 0 ? (
                                                    <span
                                                        style={{
                                                            fontSize: 11,
                                                            background: "#dcfce7",
                                                            color: "#15803d",
                                                            padding: "2px 7px",
                                                            borderRadius: 99,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        ✓
                                                    </span>
                                                ) : (
                                                    <span
                                                        style={{ color: "var(--text-muted)", fontSize: 12 }}
                                                    >
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "0.35rem",
                                                        justifyContent: "flex-end",
                                                    }}
                                                >
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="View / edit syllabus"
                                                        onClick={() => navigate(`/subjects/${sub._id}`)}
                                                    >
                                                        <FiEye size={12} /> View
                                                    </button>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="Edit subject"
                                                        onClick={() =>
                                                            navigate(`/subjects/${sub._id}/edit`)
                                                        }
                                                    >
                                                        <FiEdit2 size={12} />
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        title="Delete"
                                                        onClick={() => setDeleteTarget(sub)}
                                                    >
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Subject"
                    message={`Delete "${deleteTarget.name}"? This cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
